/// <reference types="@types/google.maps" />
import { Component, DestroyRef, ElementRef, EventEmitter, Input, Output, ViewChild, forwardRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { filter, map, startWith, switchMap, tap } from 'rxjs';
import { GoogleMapsAutocompleteService } from 'app/google-maps-autocomplete/services/google-maps-autocomplete.service';
import { GoogleMapsAutocompleteModule } from 'app/google-maps-autocomplete/google-maps-autocomplete.module';
import { MdbAutocompleteModule } from 'mdb-angular-ui-kit/autocomplete';
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import { MapsAPILoaderService } from 'app/google-maps-autocomplete/services/maps-api-loader.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-mdb-input-google-maps-autocomplete',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, GoogleMapsAutocompleteModule, MdbFormsModule, MdbAutocompleteModule],
  templateUrl: './mdb-input-google-maps-autocomplete.component.html',
  styleUrl: './mdb-input-google-maps-autocomplete.component.scss',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MdbInputGoogleMapsAutocompleteComponent),
    multi: true,
  }]
})
export class MdbInputGoogleMapsAutocompleteComponent {
  destroyRef = inject(DestroyRef)
  @ViewChild('input') input: ElementRef;
  @ViewChild('label') labelElementRef: ElementRef;

  @Input() autocomplete: string | null = null;
  @Input() label: string = '';
  @Input() id: string = '';
   
  @Input()
   set addressObj(value: any) {
    if(value){
      this.addressObject = value
      this.onChangeCallback(this.addressObject)
    }
   }

  @Input() placeholder: string = '';

  @Input()
  set value(value: any) {
    this.control.setValue({
      searchString: value.trim()
    }, {
      emitEvent: false
    });
  }

  get value(): any {
    const { value } = this.control;
    return value ? value: '';
  }

  @Input() readonly: boolean = false;

  @Output()
  public blur = new EventEmitter();

  private onChangeCallback = (value: any) => {};
  private onTouchedCallback = () => {};
  
  public control = new UntypedFormControl();
  public result = [];
  public notFound = true;

  public addressObject = null;
  public addressText = '';
  
  constructor(
    private _elementRef: ElementRef<HTMLElement>,
    private googleMapsAutocompleteService: GoogleMapsAutocompleteService,
    public mapsAPILoader: MapsAPILoaderService,
  ) { }

  async ngOnInit() {
    this.mapsAPILoader.loadPlaces();
  }

  ngAfterViewInit(): void {
    const input = this.input.nativeElement;
    const label = this.labelElementRef.nativeElement;
    if(this.value !== '') {
      input.classList.add('active');
    }

    this.control.valueChanges.pipe(
      filter(() => this.mapsAPILoader.loadedPlaces),
      map(value => {
        return value instanceof Object ? value.searchString: value;
      }),
      switchMap((value) => {
        const { Place, AutocompleteSessionToken, AutocompleteSuggestion } = google.maps.places;
        let request = {
          input: value,
          language: "en-US",
          region: "us",
          includedPrimaryTypes: ["street_address", "premise"],
          sessionToken: null
        };
        const token = new AutocompleteSessionToken();
        request.sessionToken = token;
        return AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      }),
      map(({ suggestions }) => {
        return suggestions.map((suggestion) => {
          const placePrediction = suggestion.placePrediction;
          const place = placePrediction.toPlace();
          return {
            searchString: placePrediction.text.toString(),
            place,
            data: { id: null }
          }
        })
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((results: any[]) => {
      this.result = results;
      results.length > 0 ? (this.notFound = false) : (this.notFound = true)
    })

    this.control.valueChanges.pipe(
      filter(value => value instanceof Object),
      filter(value => value.hasOwnProperty('place'))
    ).subscribe(async (value) => {
      await value.place.fetchFields({
        fields: ['addressComponents', 'displayName', 'formattedAddress'],
      })
      const { street1, street2, city, zip, state, country } = window['utils'].parseGoogleStreetAddress(value.place.addressComponents)
      this.addressObject = {
        formatted_address: value.place.formattedAddress,
        street1,
        street2,
        city,
        zip,
        state,
        country,
      }

      this.addressText = this.googleMapsAutocompleteService.getAddressText(this.addressObject);

      this.control.setValue({
        searchString: this.addressText
      }, {
        emitEvent: false
      });

      return this.onChangeCallback(this.addressObject);
    });

    this.control.valueChanges.pipe(
      filter(value => value !== null),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((value)=>{
      if(this.onChangeCallback){
        this.toggleClassActive(value, input);
        this.toggleClassActive(value, label);
      }
    })
  }

  setDescribedByIds(ids: string[]) {
    const controlElement = this._elementRef.nativeElement
      .querySelector('.app-input-container')!;
    controlElement.setAttribute('aria-describedby', ids.join(' '));
  }

  ngOnDestroy(): void {
  }

  toggleClassActive(value, element) {
    const { classList } = element;
    return value == '' ? classList.remove('active'): classList.add('active');
  }

  registerOnChange(fn: any) { 
    this.onChangeCallback = fn;
  }
  registerOnTouched(fn: () => {}): void {
    this.onTouchedCallback = fn;
  }
  writeValue(value) {
    this.addressText = this.googleMapsAutocompleteService.getAddressText(value);
    this.addressObject = this.addressText === "" ? null: value;
    this.control.setValue({
      searchString: this.addressText
    }, { emitEvent: false });
  }

  setAutocomplete(autocomplete) {
    if (autocomplete === 'off') {
      this.input.nativeElement.setAttribute('autocomplete', 'chrome-off');
    }
  }

  displayValue(value: any): string {
    return value ? value.searchString : '';
  }

  onClearAddress() {
    this.addressObject = null;
    this.addressText = '';
    this.control.setValue('');

    this.toggleClassActive(this.addressText, this.input.nativeElement);
    this.toggleClassActive(this.addressText, this.labelElementRef.nativeElement);

    this.onChangeCallback(this.addressObject);
  }

}
